import { Injectable } from '@angular/core';
import { TenantConfig, CommonValues, Location, Grade } from './app.config';

declare global {
  interface Window {
    runtimeConfig: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private tenantConfig: TenantConfig;

  constructor() {
    const defaultConfig = {
      id: 'default',
      name: 'ERP System',
      displayName: 'ERP System',
      shortName: 'ERP',
      logo: {
        primary: '',
        secondary: '',
        favicon: ''
      },
      branding: {
        primaryColor: '#1976d2',
        secondaryColor: '#dc004e',
        accentColor: '#f50057'
      },
      commonValues: {
        locations: [
          { id: 5, name: 'Stock Entrance' },
          { id: 6, name: 'Warehouse' },
          { id: 7, name: 'QC' },
          { id: 8, name: 'Repair' },
          { id: 10, name: 'Stores' }
        ],
        grades: [
          { id: 1, name: 'A' },
          { id: 2, name: 'B' }
        ]
      }
    };

    // Get tenant config from runtime config
    const tenantConfig = window.runtimeConfig?.tenant || defaultConfig;

    // Merge commonValues from root level
    const commonValues = window.runtimeConfig?.commonValues || tenantConfig.commonValues || defaultConfig.commonValues;

    this.tenantConfig = {
      ...tenantConfig,
      commonValues: commonValues
    };
  }

  getTenantConfig(): TenantConfig {
    return this.tenantConfig;
  }

  getTenantId(): string {
    return window.runtimeConfig?.tenant?.id || this.tenantConfig?.id || 'default';
  }

  isFeatureEnabled(featureName: string): boolean {
    // Priority order:
    // 1. Separately loaded tenant features (can be updated without rebuild)
    // 2. Features in runtime config (fallback)
    // 3. Features in tenant config (fallback)

    const tenantFeatures = (window as any).tenantFeatures || {};
    const configFeatures = window.runtimeConfig?.features || {};
    const configTenantFeatures = window.runtimeConfig?.tenant?.features || this.tenantConfig?.features || {};

    // Check separately loaded features first (highest priority)
    if (typeof tenantFeatures[featureName] !== 'undefined') {
      return !!tenantFeatures[featureName];
    }
    // Fall back to config features
    if (typeof configFeatures[featureName] !== 'undefined') {
      return !!configFeatures[featureName];
    }
    if (typeof configTenantFeatures[featureName] !== 'undefined') {
      return !!configTenantFeatures[featureName];
    }
    return false;
  }

  getTenantName(): string {
    return this.tenantConfig.name;
  }

  getTenantDisplayName(): string {
    return this.tenantConfig.displayName;
  }

  getTenantShortName(): string {
    return this.tenantConfig.shortName;
  }

  getTenantLogo(): { primary: string; secondary: string; favicon: string } {
    return this.tenantConfig.logo;
  }

  getCommonValues(): CommonValues {
    return this.tenantConfig.commonValues || {
      locations: [],
      grades: []
    };
  }

  getLocations(): Location[] {
    return this.tenantConfig.commonValues?.locations || [];
  }

  getGrades(): Grade[] {
    return this.tenantConfig.commonValues?.grades || [];
  }
}
